/**
 * Preparing a project's videos for reconstruction.
 *
 * The user's experience is meant to be *video → hazırlanıyor → hazır*. What
 * actually happens underneath - a second, differently-tuned frame selection
 * stored beside the one face analysis uses - is a fact about this codebase and
 * not about their capture, so none of it reaches the screen.
 *
 * Three properties this owes the rest of the app:
 *
 * **Only what is missing.** The list comes from the server's readiness check,
 * which already excludes videos holding a current reconstruction selection.
 * Re-preparing one would burn minutes of decoding to produce a duplicate run.
 *
 * **One preparation at a time.** A second click while the first pass is in
 * flight must do nothing at all - not queue, not restart. Two passes over one
 * video race to write two runs of the same kind, and whichever lands second
 * wins for no reason anybody could predict.
 *
 * **Progress that is counted, never estimated.** Videos finish one at a time,
 * so "2 / 3" is a fact. The backend publishes nothing about how far into a
 * single video it is, and a bar that moved on a timer would be a claim about
 * work nobody measured.
 */

import { useCallback, useRef, useState } from "react";

import { runKeyframeSelection } from "@/api/client";
import { ApiError } from "@/api/errors";
import type { UnpreparedVideo } from "@/api/types";

export type VideoPreparationState = "waiting" | "working" | "ready" | "failed";

export interface PreparationProgress {
  /** How many videos this pass set out to prepare. */
  total: number;
  /** How many have finished, successfully or not. */
  finished: number;
  /** Per video, keyed by media asset id. */
  states: Record<string, VideoPreparationState>;
  /** Videos that could not be prepared, named the way the user named them. */
  failures: { name: string; message: string }[];
}

const IDLE: PreparationProgress = { total: 0, finished: 0, states: {}, failures: [] };

/**
 * Why one video could not be prepared, in the frontend's own words.
 *
 * The server's `detail` is never used: it can name a codec, a temp directory
 * or a decoder's complaint. The kind of failure is what changes what the user
 * should do about it.
 */
export function preparationFailureMessage(error: ApiError): string {
  switch (error.kind) {
    case "not_found":
      return "Bu video artık projede değil.";
    case "validation":
      return "Bu dosyadan kare seçilemedi; video okunamıyor olabilir.";
    case "timeout":
      return "Hazırlık zaman aşımına uğradı. Uzun videolar daha fazla sürebilir.";
    case "network":
      return "Sunucuya ulaşılamadı.";
    case "unavailable":
    case "server":
      return "Sunucu bu videoyu şu anda hazırlayamadı.";
    default:
      return "Bu video hazırlanamadı.";
  }
}

export function useVideoPreparation(onFinished: () => void) {
  const [progress, setProgress] = useState<PreparationProgress>(IDLE);
  const [busy, setBusy] = useState(false);
  // A ref rather than `busy`: state updates are asynchronous, so two clicks in
  // the same tick would both read `busy === false` and both start a pass.
  const running = useRef(false);

  const prepare = useCallback(
    async (videos: UnpreparedVideo[]) => {
      if (running.current || videos.length === 0) return;
      running.current = true;
      setBusy(true);

      const states: Record<string, VideoPreparationState> = {};
      videos.forEach((video) => {
        states[video.media_asset_id] = "waiting";
      });
      setProgress({ total: videos.length, finished: 0, states: { ...states }, failures: [] });

      const failures: { name: string; message: string }[] = [];
      let finished = 0;

      // Sequential, not parallel. Frame extraction is CPU-bound work on the
      // same server, and three at once would make all three slower while
      // making the count meaningless.
      for (const video of videos) {
        states[video.media_asset_id] = "working";
        setProgress((p) => ({ ...p, states: { ...states } }));
        try {
          await runKeyframeSelection(video.media_asset_id, "reconstruction");
          states[video.media_asset_id] = "ready";
        } catch (caught) {
          states[video.media_asset_id] = "failed";
          const error =
            caught instanceof ApiError
              ? caught
              : new ApiError({ kind: "unknown", url: "", cause: caught });
          failures.push({
            name: video.original_filename,
            message: preparationFailureMessage(error),
          });
        }
        finished += 1;
        setProgress((p) => ({ ...p, finished, states: { ...states }, failures: [...failures] }));
      }

      running.current = false;
      setBusy(false);
      // Always, even when some failed: the ones that succeeded changed what
      // the project can do, and the readiness check is the only thing entitled
      // to say whether it is now enough.
      onFinished();
    },
    [onFinished],
  );

  const reset = useCallback(() => setProgress(IDLE), []);

  return { prepare, busy, progress, reset };
}
